import React, {
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { message } from 'antd';
import { BottomSheet } from 'react-spring-bottom-sheet';

import CommunityMessagesAPI from '../../../api/CommunityMessageAPI';
import { SocketAPI } from '../../../api/SocketAPI';
import Loader from '../../../components/Loader';
import {
  updateOrDeleteSelectedMessage,
  updateReplyState,
} from '../../../context/communityMessageReducer';
import showAppError from '../../../shared/error';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../shared/hooks';
import { ICommunityMessageSender } from '../../../types/communityMessagesTypes';
import { SOCKET_ACTION_EVENTS } from '../../../types/socketTypes';
import CommunityUserCard from './CommunityUserCard';

type Props = {
  messageId: string | null;
  roomId: string | null;
  closeModal: () => void;
  isOnlyReaction: boolean;
  preSelectedReaction: string | null;
  isReplyInfoOpen: boolean;
};

const InfoMessageModel: React.FC<Props> = ({
  messageId,
  roomId,
  closeModal,
  isOnlyReaction,
  preSelectedReaction,
  isReplyInfoOpen,
}) => {
  const dispatch = useAppDispatch();
  const { replyState } = useAppSelector((state) => state?.communityMessage);
  const [reactionVsUserObj, setReactionVsObj] = useState<{
    [key: string]: ICommunityMessageSender[];
  } | null>(null);
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [selectedReaction, setSelectedReaction] = useState(
    Boolean(preSelectedReaction) && preSelectedReaction
      ? preSelectedReaction
      : 'all',
  );

  const handleRemoveReaction = useCallback(() => {
    if (!roomId || !messageId) return;
    SocketAPI.emit(
      SOCKET_ACTION_EVENTS.ROOM_MESSAGE_REACT,
      {
        room: roomId,
        messageId: messageId,
        reaction: '',
      },
      (resp: any) => {
        if (resp?.status === 'success') {
          if (isReplyInfoOpen) {
            const newReplyState = replyState?.replies?.map((each) => {
              if (each?._id === messageId) {
                return resp.result;
              }
              return each;
            });

            dispatch(updateReplyState({ replies: newReplyState }));
          } else {
            dispatch(
              updateOrDeleteSelectedMessage(resp.result, false, () => {}),
            );
          }
          closeModal();
        } else if (resp?.status === 'error') {
          message.error(resp?.message);
        }
      },
    );
  }, [
    roomId,
    messageId,
    isReplyInfoOpen,
    closeModal,
    replyState?.replies,
    dispatch,
  ]);

  const displayUserList = useCallback(() => {
    if (!reactionVsUserObj) return;
    if (selectedReaction === 'all') {
      return Object.values(reactionVsUserObj)
        .flat()
        .map((each) => {
          if (isOnlyReaction) {
            if (each?.reaction) {
              return (
                <CommunityUserCard
                  user={{ ...each, profilePicType: 'image' }}
                  isClickable={false}
                  onClick={() => {}}
                  isOnlyReaction={isOnlyReaction}
                  handleRemoveReaction={() => {
                    handleRemoveReaction();
                  }}
                />
              );
            }
            return null;
          }
          return (
            <CommunityUserCard
              user={{ ...each, profilePicType: 'image' }}
              isClickable={false}
              onClick={() => {}}
            />
          );
        });
    }
    return reactionVsUserObj[selectedReaction].map((each) => (
      <CommunityUserCard
        user={{ ...each, profilePicType: 'image' }}
        isClickable={false}
        onClick={() => {}}
        isOnlyReaction={isOnlyReaction}
        handleRemoveReaction={() => {
          handleRemoveReaction();
        }}
      />
    ));
  }, [
    handleRemoveReaction,
    isOnlyReaction,
    reactionVsUserObj,
    selectedReaction,
  ]);

  const displayTabs = useCallback(() => {
    if (!reactionVsUserObj) return;
    return (
      <>
        {
          <button
            className={`${selectedReaction === 'all' ? 'selected' : ''}`}
            onClick={() => setSelectedReaction('all')}>
            <p>All {totalUserCount}</p>
          </button>
        }
        {Object.entries(reactionVsUserObj)
          .filter(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ([emoji, _list]: [string, ICommunityMessageSender[]]) =>
              emoji !== 'onlySeen',
          )
          .map(([emoji, list]: [string, ICommunityMessageSender[]]) => (
            <button
              className={`${selectedReaction === emoji ? 'selected' : ''}`}
              onClick={() => setSelectedReaction(emoji)}>
              <p>
                {emoji} {list?.length}
              </p>
            </button>
          ))}
      </>
    );
  }, [reactionVsUserObj, selectedReaction, totalUserCount]);

  const fetchSeenAndReactionUserList = useCallback(async () => {
    if (!(messageId && roomId)) return;
    try {
      const resp = await CommunityMessagesAPI.getSeenAndReactionUserList(
        roomId,
        messageId,
      );
      if (resp?.status === 200) {
        const reactionObj = resp.data?.result.reduce(
          (acc: { [key: string]: ICommunityMessageSender[] }, eachUser) => {
            const reaction = (eachUser?.reaction as string) || 'onlySeen';

            const reactionUserList = acc?.[reaction];
            if (reactionUserList) {
              acc[reaction] = [...acc[reaction], eachUser];
            } else {
              acc[reaction] = [eachUser];
            }
            return acc;
          },
          {},
        );
        if (isOnlyReaction) {
          const { onlySeen, ...rest } = reactionObj;
          setTotalUserCount(Object.values(rest).flat().length);
        } else {
          setTotalUserCount(resp?.data?.result?.length);
        }
        setReactionVsObj(reactionObj);
      } else {
        showAppError(resp?.data);
      }
    } catch (error) {
      showAppError(error);
    }
  }, [isOnlyReaction, messageId, roomId]);

  useEffect(() => {
    if (messageId && roomId) {
      fetchSeenAndReactionUserList();
    }
  }, [fetchSeenAndReactionUserList, messageId, roomId]);

  if (!reactionVsUserObj && Boolean(messageId)) {
    return (
      <Loader size="small" className="input-wrapper__actions__sending-loader" />
    );
  }

  return (
    <div>
      <BottomSheet
        open={Boolean(messageId)}
        onDismiss={() => closeModal()}
        style={{ zIndex: 100, minHeight: 200 }}>
        <div className="info-wrapper" style={{ minHeight: 200 }}>
          {reactionVsUserObj && Object.keys(reactionVsUserObj)?.length ? (
            <>
              <div className="info-wrapper_tabs">{displayTabs()}</div>
              <div className="info-wrapper_users">{displayUserList()}</div>
            </>
          ) : null}
        </div>
      </BottomSheet>
    </div>
  );
};

export default memo(InfoMessageModel);
